import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';

const formatDateTick = (value) => {
  if (!value) return '';
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return `${raw.slice(2, 4)}.${raw.slice(5, 7)}`;
  }
  return raw;
};

const formatDateTooltip = (value) => {
  if (!value) return '-';
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw;
  }
  return raw;
};

const toNum = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const asNumberText = (value) => (value == null ? '-' : Number(value).toLocaleString());

function WeeklyFlowComboChart({ rows = [] }) {
  const chartOption = useMemo(() => {
    const xData = rows.map((row) => row.baseDate);
    const salePriceData = rows.map((row) => toNum(row.salePrice));
    const leasePriceData = rows.map((row) => toNum(row.leasePrice));
    const saleCountData = rows.map((row) => toNum(row.saleCount));
    const leaseCountData = rows.map((row) => toNum(row.leaseCount));
    const leaseRatioData = rows.map((row) => {
      const salePrice = toNum(row.salePrice);
      const leasePrice = toNum(row.leasePrice);
      if (salePrice && leasePrice && salePrice > 0) {
        return (leasePrice / salePrice) * 100;
      }
      return null;
    });

    const priceValues = [...salePriceData, ...leasePriceData].filter((v) => v !== null);
    const countValues = [...saleCountData, ...leaseCountData].filter((v) => v !== null);
    const ratioValues = leaseRatioData.filter((v) => v !== null);

    const priceMinRaw = priceValues.length > 0 ? Math.min(...priceValues) : 0;
    const priceMaxRaw = priceValues.length > 0 ? Math.max(...priceValues) : 100;
    const priceGap = Math.max(1, priceMaxRaw - priceMinRaw);
    const pricePad = Math.ceil(priceGap * 0.12);
    const priceMin = Math.max(0, Math.floor((priceMinRaw - pricePad) / 1000) * 1000);
    const priceMax = Math.ceil((priceMaxRaw + pricePad) / 1000) * 1000;

    const countMax = countValues.length > 0 ? Math.max(...countValues) : 10;
    const countAxisMax = Math.max(10, Math.ceil(countMax * 1.08));

    const ratioMin = ratioValues.length > 0 ? Math.floor(Math.min(...ratioValues) - 2) : 0;
    const ratioMax = ratioValues.length > 0 ? Math.ceil(Math.max(...ratioValues) + 2) : 100;

    return {
      animationDuration: 550,
      grid: [
        {
          left: 44,
          right: 44,
          top: 30,
          height: '50%',
          containLabel: true,
        },
        {
          left: 44,
          right: 44,
          top: '66%',
          height: '25%',
          containLabel: true,
        },
      ],
      axisPointer: {
        link: [{ xAxisIndex: [0, 1] }],
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'line',
          lineStyle: {
            color: '#94a3b8',
            width: 1,
            type: 'dashed',
          },
        },
        formatter: (params = []) => {
          if (!Array.isArray(params) || params.length === 0) return '';
          const dateText = formatDateTooltip(params[0]?.axisValue);
          const salePrice = params.find((p) => p?.seriesName === '매매가')?.data;
          const leasePrice = params.find((p) => p?.seriesName === '전세가')?.data;
          const leaseRatio = params.find((p) => p?.seriesName === '전세가율')?.data;
          const saleCount = params.find((p) => p?.seriesName === '매물수')?.data;
          const leaseCount = params.find((p) => p?.seriesName === '전세수')?.data;

          return [
            `<div style="font-weight:700;margin-bottom:6px;">${dateText}</div>`,
            `<div style="margin-bottom:2px;">매매가: <strong style="color:#dc2626">${asNumberText(salePrice)}</strong></div>`,
            `<div style="margin-bottom:2px;">전세가: <strong style="color:#1d4ed8">${asNumberText(leasePrice)}</strong></div>`,
            `<div style="margin-bottom:2px;">전세가율: <strong style="color:#8b5cf6">${leaseRatio != null ? leaseRatio.toFixed(1) + '%' : '-'}</strong></div>`,
            `<div style="margin-bottom:2px;">매물수: <strong style="color:#f59e0b">${asNumberText(saleCount)}</strong></div>`,
            `<div>전세수: <strong style="color:#10b981">${asNumberText(leaseCount)}</strong></div>`,
          ].join('');
        },
      },
      xAxis: [
        {
          type: 'category',
          boundaryGap: false,
          data: xData,
          axisLabel: { show: false },
          axisTick: { show: false },
          axisLine: { show: false },
        },
        {
          type: 'category',
          boundaryGap: true,
          gridIndex: 1,
          data: xData,
          axisLabel: {
            color: '#52627d',
            fontSize: 11,
            formatter: formatDateTick,
            hideOverlap: true,
            margin: 10,
          },
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#dce4ee' } },
        },
      ],
      yAxis: [
        {
          type: 'value',
          min: priceMin,
          max: priceMax,
          axisLabel: {
            color: '#334155',
            fontSize: 11,
            formatter: (v) => Number(v).toLocaleString(),
          },
          splitLine: {
            lineStyle: { color: '#e8edf4' },
          },
          axisLine: { show: false },
          axisTick: { show: false },
        },
        {
          type: 'value',
          min: ratioMin,
          max: ratioMax,
          axisLabel: {
            color: '#8b5cf6',
            fontSize: 11,
            formatter: (v) => v.toFixed(0) + '%',
          },
          splitLine: { show: false },
          axisLine: { show: false },
          axisTick: { show: false },
        },
        {
          type: 'value',
          gridIndex: 1,
          min: 0,
          max: countAxisMax,
          axisLabel: {
            color: '#64748b',
            fontSize: 10,
            formatter: (v) => Number(v).toLocaleString(),
          },
          splitLine: { show: false },
          axisLine: { show: false },
          axisTick: { show: false },
        },
      ],
      series: [
        {
          name: '매매가',
          type: 'line',
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: salePriceData,
          smooth: 0.32,
          symbol: 'none',
          connectNulls: true,
          lineStyle: { width: 2.5, color: '#dc2626' },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(220,38,38,0.16)' },
                { offset: 1, color: 'rgba(220,38,38,0)' },
              ],
            },
          },
          z: 3,
        },
        {
          name: '전세가',
          type: 'line',
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: leasePriceData,
          smooth: 0.32,
          symbol: 'none',
          connectNulls: true,
          lineStyle: { width: 2.5, color: '#1d4ed8' },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(29,78,216,0.14)' },
                { offset: 1, color: 'rgba(29,78,216,0)' },
              ],
            },
          },
          z: 3,
        },
        {
          name: '전세가율',
          type: 'line',
          xAxisIndex: 0,
          yAxisIndex: 1,
          data: leaseRatioData,
          smooth: 0.32,
          symbol: 'circle',
          symbolSize: 4,
          connectNulls: true,
          lineStyle: { width: 2, color: '#8b5cf6', type: 'solid' },
          itemStyle: { color: '#8b5cf6' },
          z: 4,
        },
        {
          name: '매물수',
          type: 'bar',
          xAxisIndex: 1,
          yAxisIndex: 2,
          data: saleCountData,
          barWidth: 6,
          barGap: '20%',
          barMinHeight: 4,
          itemStyle: {
            color: '#f59e0b',
            borderRadius: [3, 3, 0, 0],
            opacity: 0.86,
          },
          z: 2,
        },
        {
          name: '전세수',
          type: 'bar',
          xAxisIndex: 1,
          yAxisIndex: 2,
          data: leaseCountData,
          barWidth: 6,
          barMinHeight: 4,
          itemStyle: {
            color: '#10b981',
            borderRadius: [3, 3, 0, 0],
            opacity: 0.84,
          },
          z: 2,
        },
      ],
    };
  }, [rows]);

  return (
    <ReactECharts
      option={chartOption}
      style={{ width: '100%', height: '320px' }}
      opts={{ renderer: 'canvas' }}
      notMerge
      lazyUpdate
    />
  );
}

export default WeeklyFlowComboChart;
